<template>
  <RevCard class="p-28">
    <ShippingMarket :marketCountryCode="shippingMethod.market" />
    <ShippingInfo />

    <template v-if="!initialValues">
      <ShippingTable
        v-if="shippingMethod.marketShippingMethods.length"
        :marketShippingMethod="shippingMethod.marketShippingMethods"
        @deleted="handleDeleted"
        @edit="(formValues) => handleEdit(formValues, shippingMethod.market)"
      />

      <RevButton
        class="mt-24"
        data-qa="add_shipping_method"
        full-width="adaptive"
        variant="secondary"
        @click="handleAdd(shippingMethod.market)"
      >
        {{ i18n(translations.add) }}
      </RevButton>
    </template>

    <Form
      v-if="initialValues"
      :initialValues
      :isEditing
      :isSubmitting
      :should-show-market-select="false"
      @cancel="handleCancel"
      @submit="handleSubmit"
    />
  </RevCard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { ProblemValidation } from '@backmarket/http-api'
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'

import translations from '../../ShippingLegacy.translations'
import type {
  MarketShippingMethod,
  ShippingMethod,
} from '../../ShippingLegacy.types'
import { toFormValues } from '../../ShippingLegacy.utils'
import { useShippingMethodApi } from '../../composables/useShippingMethodApi'
import { DEFAULT_INITIAL_VALUES } from '../Form/Form.constants'
import type { FormValues } from '../Form/Form.types'
import Form from '../Form/Form.vue'
import ShippingInfo from '../ShippingInfo/ShippingInfo.vue'
import ShippingMarket from '../ShippingMarket/ShippingMarket.vue'
import ShippingTable from '../ShippingTable/ShippingTable.vue'

const emit = defineEmits(['deleted', 'edited-or-created'])
defineProps<{ shippingMethod: ShippingMethod }>()

const i18n = useI18n()
const { openErrorToast } = useTheToast()
const { editShippingMethod, createShippingMethod } = useShippingMethodApi()
const isSubmitting = ref(false)

const initialValues = ref<null | FormValues>(null)
const isEditing = computed(() => Boolean(initialValues.value?.id))

function handleAdd(market: MarketCountryCode) {
  initialValues.value = { ...DEFAULT_INITIAL_VALUES, market }
}

function handleEdit(
  marketShippingMehtod: MarketShippingMethod,
  market: MarketCountryCode,
) {
  initialValues.value = toFormValues(marketShippingMehtod, market)
}

function handleDeleted() {
  emit('deleted')
}

function handleCancel() {
  initialValues.value = null
}

async function handleSubmit(formValues: FormValues) {
  isSubmitting.value = true
  try {
    if (isEditing.value) {
      await editShippingMethod(formValues)
    } else {
      await createShippingMethod(formValues)
    }
    initialValues.value = null
    emit('edited-or-created')
  } catch (error) {
    openErrorToast({
      title: (error as ProblemValidation).title,
      content: (error as ProblemValidation).errors?.[0]?.title,
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>
